import { memo } from "react";
import { makeStyles } from "tss-react/mui";
import { getPhoneNumber, toFormattedDate } from "@miplatform/utils";
import { Box, Typography } from "@mui/material";

/**
 * A card component displaying previous customer information inside a tooltip.
 *
 * @param {Object} props - Component props.
 * @param {string} props.accountName - The name of the previous customer.
 * @param {Array} props.accountChanges - The account change history for the home.
 * @param {string} [props.phone] - The phone number of the previous customer's contact.
 * @param {string} [props.email] - The email of the previous customer's contact.
 *
 * @returns {JSX.Element} The rendered card with previous customer details.
 */
export const PreviousCustomerInfoCard = memo(({ accountChanges = [] }) => {
  const { classes, theme } = useStyles();

  const latestChange = accountChanges[accountChanges.length - 1];

  if (!latestChange) return null;

  const {
    oldAccountName,
    oldAccountPhone,
    oldAccountEmail,
    oldAccountFrom,
    oldAccountTo,
  } = latestChange;
  const dateRange = `${toFormattedDate(
    oldAccountFrom,
    "MMM D, YYYY"
  )} - ${toFormattedDate(oldAccountTo, "MMM D, YYYY")}`;

  return (
    <Box className={classes.card}>
      <Box className={classes.header}>
        <Typography
          fontSize={theme.fontSize.medium}
          fontWeight={theme.fontWeight.semiBold}
          color={theme.palette.common.white}
        >
          Previous Customer
        </Typography>
      </Box>
      <Box className={classes.content}>
        <Typography
          color="textPrimary"
          fontWeight={theme.fontWeight.semiBold}
          fontSize={theme.fontSize.medium}
        >
          {oldAccountName || "--"}
        </Typography>
        <Typography
          fontSize={theme.fontSize.small}
          color={theme.palette.text.secondary}
        >
          {dateRange}
        </Typography>
        {oldAccountPhone && (
          <Typography color="textPrimary" fontSize={theme.fontSize.small}>
            <strong>Phone:</strong> {getPhoneNumber(oldAccountPhone)}
          </Typography>
        )}
        {oldAccountEmail && (
          <Typography color="textPrimary" fontSize={theme.fontSize.small}>
            <strong>Email:</strong> {oldAccountEmail}
          </Typography>
        )}
      </Box>
    </Box>
  );
});

const useStyles = makeStyles()((theme) => ({
  card: {
    borderRadius: theme.spacing(1),
    overflow: "hidden",
    minWidth: 240,
    border: `1px solid ${theme.palette.grey.mediumLight}`,
    backgroundColor: theme.palette.common.white,
  },
  header: {
    backgroundColor: theme.palette.primary.main,
    padding: theme.spacing(1, 2),
  },
  content: {
    padding: theme.spacing(1.5, 2),
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(0.5),
  },
}));
